import { getApi } from "../client/apiClient";
import type { HttpClient } from "../client/types";
import type {
  GenerateManagementReportNewRequest,
  GenerateManagementReportRequest,
  GenerateManagementYearSummaryReportRequest,
  GenerateConsolidatedReportRequest,
  GenerateNewsReportRequest,
  GenerateSwimmersReportRequest,
} from "../client/types/report.types";

const createReportEndpoints = (client: HttpClient = getApi()) => ({
  /**
   * Generate and download an Excel report for swimmers registered in a specific date range.
   *
   * @param params - Date range filter parameters (start_date, end_date in DD/MM/YYYY format, optional building_id)
   * @returns Promise that resolves to a Blob containing the Excel file
   */
  generateSwimmersReport: async (
    params: GenerateSwimmersReportRequest
  ): Promise<Blob> => {
    const { data } = await client.get<Blob>("/reports/swimmers", {
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        format: params.format || "excel",
        ...(params.building_id && { building_id: params.building_id }),
      },
      responseType: "blob",
    });
    return data;
  },
  /**
   * Generate and download an Excel report for news registered in a specific date range.
   *
   * @param params - Date range filter parameters (start_date, end_date in DD/MM/YYYY format, optional building_id)
   * @returns Promise that resolves to a Blob containing the Excel file
   */
  generateNewsReport: async (
    params: GenerateNewsReportRequest
  ): Promise<Blob> => {
    const { data } = await client.get<Blob>("/reports/news", {
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        format: params.format || "excel",
        ...(params.building_id && { building_id: params.building_id }),
        ...(params.report_type_id && { report_type_id: params.report_type_id }),
      },
      responseType: "blob",
    });
    return data;
  },

  /** Generate the all-buildings consolidated Excel workbook. */
  generateConsolidatedReport: async (
    params: GenerateConsolidatedReportRequest
  ): Promise<Blob> => {
    const { data } = await client.get<Blob>("/reports/consolidated", {
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        ...(params.lifeguard_status_filter && {
          lifeguard_status_filter: params.lifeguard_status_filter,
        }),
        ...(params.building_status_filter && {
          building_status_filter: params.building_status_filter,
        }),
      },
      responseType: "blob",
    });
    return data;
  },

  /**
   * Generate and download the management report PDF (reporte gerencial) for a building and date range.
   *
   * @param params - start_date, end_date (DD/MM/YYYY), building_id (required), optional year_summary (0 | 1)
   * @returns Promise that resolves to a Blob containing the PDF
   */
  generateManagementReport: async (
    params: GenerateManagementReportRequest
  ): Promise<Blob> => {
    const { data } = await client.get<Blob>("/reports/management", {
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        building_id: params.building_id,
        // Query params are sent as strings; backend parses year_summary to int (0 or 1)
        ...(params.year_summary !== undefined && {
          year_summary: String(params.year_summary),
        }),
      },
      responseType: "blob",
    });
    return data;
  },

  /**
   * Generate and download the new management report PDF (refactored template).
   *
   * Uses the new template with parametrized interpretation pages and dynamic
   * risk-event tables. Same parameters as generateManagementReport.
   *
   * @param params - start_date, end_date (DD/MM/YYYY), building_id (required), optional year_summary (0 | 1)
   * @returns Promise that resolves to a Blob containing the PDF
   */
  generateManagementReportNew: async (
    params: GenerateManagementReportNewRequest
  ): Promise<Blob> => {
    const { data } = await client.get<Blob>("/reports/management-new", {
      params: {
        start_date: params.start_date,
        end_date: params.end_date,
        building_id: params.building_id,
        ...(params.year_summary !== undefined && {
          year_summary: String(params.year_summary),
        }),
      },
      responseType: "blob",
    });
    return data;
  },

  /**
   * Generate and download the 3-page management yearly summary PDF
   * (cover, yearly summary, closing) for a given building and calendar year.
   *
   * @param params - year (YYYY) and building_id (UUID)
   * @returns Promise that resolves to a Blob containing the PDF
   */
  generateManagementYearSummaryReport: async (
    params: GenerateManagementYearSummaryReportRequest
  ): Promise<Blob> => {
    const { data } = await client.get<Blob>("/reports/management/year-summary", {
      params: {
        year: params.year,
        building_id: params.building_id,
      },
      responseType: "blob",
    });
    return data;
  },
});

export type ReportEndpoints = ReturnType<typeof createReportEndpoints>;

export const reportEndpoints = createReportEndpoints();

